function getFocusableElements(container) {
  if (!container) {
    console.warn('getFocusableElements: container is null');
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (!container) {
    console.warn('trapFocus: container is null');
    return;
  }

  const elements = getFocusableElements(container);
  if (!elements.length) return;

  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key

    // On the last focusable element and tab forward, focus the first element
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    // On the first focusable element and tab backward, focus the last element
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  // Try focusing element safely
  requestAnimationFrame(() => {
    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      elementToFocus.focus();

      if (
        elementToFocus.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    }
  });
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this._silentUpdate = false;

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();

    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.handleExternalQuantityUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();

    if (this._silentUpdate) return;

    const newQty = parseInt(this.input.value, 10);

    // publish(PUB_SUB_EVENTS.quantityUpdate, {
    //   source: this,
    //   data: { quantity: parseInt(this.input.value, 10) },
    // });

    document.dispatchEvent(new CustomEvent('quantity:changed', {
      detail: { quantity: newQty }
    }));

    // this.checkQuotaLimit(newQty);
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);

      const newQty = parseInt(this.input.value, 10) || 0;

      document.dispatchEvent(new CustomEvent('quantity:changed', {
        detail: { quantity: newQty }
      }));

      // this.checkQuotaLimit(newQty);
    }

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  handleExternalQuantityUpdate(message) {
    if (message?.source === this) return;

    const incoming = message?.data?.quantity;
    if (incoming === undefined || incoming === null) return;

    const incomingQty = parseInt(incoming, 10);
    if (Number.isNaN(incomingQty)) return;

    const current = parseInt(this.input.value, 10);
    if (current === incomingQty) return;

    this._silentUpdate = true;
    this.input.value = incomingQty;
    this.validateQtyRules();
    this._silentUpdate = false;
  }

  validateQtyRules() {
    const value = parseInt(this.input.value, 10) || 0;

    if (this.input.min) {
      const min = parseInt(this.input.min, 10);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) buttonMinus.classList.toggle('disabled', value <= min);
    }

    if (this.input.max) {
      const max = parseInt(this.input.max, 10);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
    }
  }

  checkQuotaLimit(newQty) {
    const sku = this.input.dataset.productSku;
    if (!sku) return;

    const quotaEl = document.querySelector(`.quota-data[data-sku="${sku}"]`);
    if (!quotaEl) return;

    let quota;
    try {
      quota = JSON.parse(quotaEl.textContent);
    } catch {
      return;
    }



    const limit = quota.monthly_limit ?? quota.quarterly_limit ?? quota.yearly_limit ?? quota.max ?? 0;

    const addBtn = document.querySelector(`button.product-form__submit[data-product-sku="${CSS.escape(sku)}"]`);
    if (!addBtn) return;

    const span = addBtn.querySelector('span');
    const limitText = window.variantStrings?.limitReached || 'Limit Reached';
    const addText = window.variantStrings?.addToCart || 'Add to cart';

    if (newQty > limit) {
      // addBtn.setAttribute('disabled', 'true');
      // addBtn.classList.add('disabled');
      // if (span) span.textContent = limitText;
    } else {
      addBtn.removeAttribute('disabled');
      addBtn.classList.remove('disabled');
      if (span) span.textContent = addText;
    }
  }

}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      // details.removeAttribute('open');
      // details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.originalNavHeight = null;
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    const nav = this.querySelector('.menu-drawer__navigation');
    if (nav && !this.originalNavHeight) {
      this.originalNavHeight = nav.offsetHeight;
    }

    this.addSubmenuHeightHandler();

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');

    const nav = this.querySelector('.menu-drawer__navigation');
    if (nav && this.originalNavHeight) {
      nav.style.height = `${this.originalNavHeight}px`;
    }

    window.removeEventListener('resize', this.onResize);
  }

  addSubmenuHeightHandler() {
    const menuItems = this.querySelectorAll('[id^="Details-menu-drawer-menu-item-"]');
    const nav = this.querySelector('.menu-drawer__navigation');

    if (!nav) return;

    menuItems.forEach(item => {
      item.addEventListener('toggle', () => {
        if (item.open) {
          const submenu = item.querySelector('.menu-drawer__inner-submenu');
          if (submenu) {
            nav.style.minHeight = `${submenu.scrollHeight}px`;
          }
        } else {
          nav.style.minHeight = `fit-content`;
        }
      });
    });
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;

    const section = this.closest('.shopify-section') || this;
    this.currentPageElement = this.querySelector('.slider-counter--current') || section.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total') || section.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]') || section.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]') || section.querySelector('button[name="next"]');
    
    const featuredCollection = this.getAttribute('data-section') === 'featuredCollection' ? true : false;

    // Base direction from attribute
    if (featuredCollection) this.baseScrollDirection = (this.getAttribute('data-scroll-direction') || 'horizontal').toLowerCase();

    // Horizontal flow direction from attribute: 'ltr' (default) or 'rtl'.
    // Only affects horizontal sliders; vertical sliders ignore it.
    this.baseFlowDirection =
      (this.getAttribute('data-flow-direction') || 'ltr').toLowerCase() === 'rtl' ? 'rtl' : 'ltr';

    // Mobile breakpoint: all cases forced horizontal
    this.mobileQuery = window.matchMedia('(max-width: 989px)');

    this.isDragging = false;
    this.dragDistance = 0;
    this.dragThreshold = 5;

    if (!this.slider || !this.nextButton) return;

    // Keep previous effective direction to detect change on resize
    this.prevEffectiveDirection = this.effectiveScrollDirection;

    this.applyDirectionStyle();
    this.initPages();

    const resizeObserver = new ResizeObserver(() => this.onResize());

    resizeObserver.observe(this.slider);
    resizeObserver.observe(section);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.slider.addEventListener('mousedown', this.onDragStart.bind(this));
    window.addEventListener('mouseup', this.onDragEnd.bind(this));
    window.addEventListener('mousemove', this.onDragMove.bind(this));

    this.slider.addEventListener('dragstart', e => e.preventDefault());
    this.slider.addEventListener(
      'click',
      e => {
        if (this.dragDistance > this.dragThreshold) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  // Effective direction:
  // - Mobile => horizontal
  // - Desktop/Tablet => baseScrollDirection
  get effectiveScrollDirection() {
    return this.mobileQuery.matches ? 'horizontal' : this.baseScrollDirection;
  }

  get isVertical() {
    return this.effectiveScrollDirection === 'vertical';
  }
  get sizeProp() {
    return this.isVertical ? 'clientHeight' : 'clientWidth';
  }
  get itemSizeProp() {
    return this.isVertical ? 'clientHeight' : 'clientWidth';
  }
  get offsetProp() {
    return this.isVertical ? 'offsetTop' : 'offsetLeft';
  }
  get scrollProp() {
    return this.isVertical ? 'scrollTop' : 'scrollLeft';
  }
  get scrollToKey() {
    return this.isVertical ? 'top' : 'left';
  }
  get pageCoordProp() {
    return this.isVertical ? 'pageY' : 'pageX';
  }

  // Right-to-left flow only applies to horizontal sliders.
  get isRTL() {
    return !this.isVertical && this.baseFlowDirection === 'rtl';
  }

  // Physical start-edge coordinate of a bounding rect along the scroll axis.
  // Independent of the browser's RTL scrollLeft sign convention.
  edgeStart(rect) {
    if (this.isVertical) return rect.top;
    return this.isRTL ? rect.right : rect.left;
  }

  // Physical distance between consecutive slides, measured from live geometry
  // (getBoundingClientRect) so it is correct for LTR, RTL and vertical without
  // relying on offsetLeft/scrollWidth, which are unreliable in RTL overflow.
  sliderItemPitch() {
    const items = this.sliderItemsToShow;
    if (!items || items.length < 2) {
      return (items && items[0] && items[0][this.itemSizeProp]) || 1;
    }
    const a = this.edgeStart(items[0].getBoundingClientRect());
    const b = this.edgeStart(items[1].getBoundingClientRect());
    return Math.abs(b - a) || items[0][this.itemSizeProp] || 1;
  }

  // How far the track has scrolled from its start edge, in physical pixels
  // (~0 at the first slide, +pitch per slide). Works under any RTL scroll model.
  trackAdvance() {
    const items = this.sliderItemsToShow;
    if (!items || items.length === 0) return 0;
    const containerStart = this.edgeStart(this.slider.getBoundingClientRect());
    const firstStart = this.edgeStart(items[0].getBoundingClientRect());
    return this.isRTL ? firstStart - containerStart : containerStart - firstStart;
  }

  // Total scrollable distance along the active axis (physical px, >= 0).
  maxScrollDistance() {
    if (this.isVertical) {
      return Math.max(0, this.slider.scrollHeight - this.slider.clientHeight);
    }
    return Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
  }

  applyDirectionStyle() {
    // Pin the horizontal flow direction (RTL lays the first item on the right).
    // Only sliders that opt in via data-flow-direction are managed, so every
    // other slider keeps its inherited direction. Card content is reset in CSS.
    if (this.isRTL) {
      this.slider.setAttribute('dir', 'rtl');
    } else if (this.getAttribute('data-flow-direction')) {
      this.slider.setAttribute('dir', 'ltr');
    }

    // Reset styles first (avoid stuck styles when switching)
    this.slider.style.overflowX = '';
    this.slider.style.overflowY = '';
    this.slider.style.maxHeight = '';
    this.slider.style.height = '';

    if (this.isVertical) {
      this.slider.style.overflowY = 'auto';
      this.slider.style.overflowX = 'hidden';
      this.setVerticalHeight();
    } else {
      this.slider.style.overflowX = 'auto';
      this.slider.style.overflowY = 'hidden';
      // Horizontal should not be height-locked
      this.slider.style.maxHeight = '';
      this.slider.style.height = '';
    }
  }

  setVerticalHeight() {
    // Match height of closest Shopify section
    const section = this.closest('.shopify-section') || this.parentElement;
    if (!section) return;

    const sectionRect = section.querySelector('.collection__second-wrapper').getBoundingClientRect();
    const sectionHeight = sectionRect.height;

    // Lock slider height to section height
    if (sectionHeight > 0) {
      this.slider.style.height = `${sectionHeight}px`;
      this.slider.style.maxHeight = `${sectionHeight}px`;
    }
  }

  onResize() {
    const currentEffective = this.effectiveScrollDirection;

    // If direction changes (e.g., desktop vertical -> mobile horizontal),
    // reset scroll to avoid wrong currentPage.
    if (currentEffective !== this.prevEffectiveDirection) {
      this.prevEffectiveDirection = currentEffective;
      this.slider[this.scrollProp] = 0;
    }

    this.applyDirectionStyle();
    this.initPages();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(el =>
      this.isVertical ? el.clientHeight > 0 : el.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) {
      this.totalPages = this.sliderItemsToShow.length || 1;
      this.currentPage = 1;
      this.updateCountersOnly();
      return;
    }

    // RTL horizontal cannot trust offsetLeft/scrollWidth (unreliable in RTL
    // overflow), so it measures from live geometry. LTR and vertical keep the
    // original offset-based math unchanged.
    let leadingOffset;
    if (this.isRTL) {
      this.sliderItemOffset = this.sliderItemPitch();
      leadingOffset = 0;
    } else {
      this.sliderItemOffset =
        this.sliderItemsToShow[1][this.offsetProp] - this.sliderItemsToShow[0][this.offsetProp];

      // Guard against zero offset (rare but can happen with display/grid changes)
      if (!this.sliderItemOffset) {
        this.sliderItemOffset = this.sliderItemsToShow[0][this.itemSizeProp] || 1;
      }

      leadingOffset = this.sliderItemsToShow[0][this.offsetProp];
    }

    this.slidesPerPage = Math.floor(
      (this.slider[this.sizeProp] - leadingOffset) / this.sliderItemOffset
    );
    if (this.slidesPerPage < 1) this.slidesPerPage = 1;
    if (this.slidesPerPage > this.sliderItemsToShow.length) {
      this.slidesPerPage = this.sliderItemsToShow.length;
    }

    // Counter total: honour the value the section rendered into the counter
    // (e.g. the product count) so the pagination reaches "N / N" at the end.
    // Captured once, before the counter text is overwritten; falls back to the
    // number of slides when no total was rendered.
    if (this.declaredTotal === undefined) {
      const declared = this.pageTotalElement
        ? parseInt((this.pageTotalElement.textContent || '').trim(), 10)
        : NaN;
      this.declaredTotal = Number.isFinite(declared) && declared > 0 ? declared : 0;
    }
    this.totalPages = this.declaredTotal || this.sliderItemsToShow.length;

    this.update();
  }

  updateCountersOnly() {
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;

    // Map physical scroll progress (0 at the first slide, 1 at the end) onto the
    // 1..totalPages range, so the counter starts at 1 and reaches totalPages at
    // the very end regardless of how many slides are visible per view.
    const maxScroll = this.maxScrollDistance();
    let progress = maxScroll > 0 ? this.trackAdvance() / maxScroll : 0;
    if (progress < 0) progress = 0;
    if (progress > 1) progress = 1;
    const rawPage = Math.round(progress * (this.totalPages - 1)) + 1;
    this.currentPage = Math.min(this.totalPages, Math.max(1, rawPage));

    this.updateCountersOnly();

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement:
              this.sliderItemsToShow[
                Math.min(this.currentPage, this.sliderItemsToShow.length) - 1
              ],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.sliderItemsToShow.length > 0) {
      const first = this.sliderItemsToShow[0];
      const last = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];

      const atStart = this.isRTL
        ? this.trackAdvance() <= 1
        : this.slider[this.scrollProp] === 0;
      if (this.isSlideVisible(first) && atStart) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(last)) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
  }

  isSlideVisible(element, offset = 0) {
    // RTL uses a physical containment test (offsetLeft is unreliable in RTL
    // overflow); LTR and vertical keep the original offset-based math.
    if (this.isRTL) {
      const containerRect = this.slider.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const tolerance = 1;
      return (
        elementRect.left >= containerRect.left + offset - tolerance &&
        elementRect.right <= containerRect.right - offset + tolerance
      );
    }

    const lastVisible = this.slider[this.sizeProp] + this.slider[this.scrollProp] - offset;
    return (
      element[this.offsetProp] + element[this.itemSizeProp] <= lastVisible &&
      element[this.offsetProp] >= this.slider[this.scrollProp]
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = Number(event.currentTarget.dataset.step || 1);

    // Physical pixels to move the viewport toward the requested end. Because
    // adding to scrollLeft/scrollTop shifts the viewport the same way regardless
    // of the RTL scroll model, this stays correct for LTR, RTL and vertical.
    let amount = step * this.sliderItemOffset;
    if (event.currentTarget.name !== 'next') amount = -amount;
    if (this.isRTL) amount = -amount; // the "next" end is physically to the left in RTL

    this.slideScrollPosition = this.slider[this.scrollProp] + amount;

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      [this.scrollToKey]: position,
      behavior: 'smooth',
    });
  }

  onDragStart(e) {
    this.isDragging = true;
    this.startPos = e[this.pageCoordProp];
    this.scrollStart = this.slider[this.scrollProp];
    this.dragDistance = 0;
    this.slider.classList.add('dragging');
  }

  onDragEnd() {
    this.isDragging = false;
    this.slider.classList.remove('dragging');
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    const d = e[this.pageCoordProp] - this.startPos;
    this.dragDistance = Math.abs(d);
    this.slider[this.scrollProp] = this.scrollStart - d;
  }
}

customElements.define('slider-component', SliderComponent);


class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll('.slider-counter__link')
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener('click', this.linkToSlide.bind(this))
    );
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    this.bindDesktopSwipe();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  bindDesktopSwipe() {
    const threshold = 50;
    let startX = 0;
    let activePointerId = null;
    let isMouse = false;

    this.slider.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      if (e.target.closest('a, button')) return;

      isMouse = e.pointerType === 'mouse';
      activePointerId = e.pointerId;
      startX = e.clientX;
      try {
        this.slider.setPointerCapture(activePointerId);
      } catch (err) {}
    });

    const handleEnd = (e) => {
      if (activePointerId === null) return;
      const diff = e.clientX - startX;
      if (Math.abs(diff) > threshold) {
        if (diff < 0) {
          this.triggerNav('next');
        } else {
          this.triggerNav('previous');
        }
      }
      try { this.slider.releasePointerCapture(activePointerId); } catch (err) {}
      activePointerId = null;
    };

    this.slider.addEventListener('pointerup', handleEnd);
    this.slider.addEventListener('pointercancel', () => {
      if (activePointerId !== null) {
        try { this.slider.releasePointerCapture(activePointerId); } catch (err) {}
        activePointerId = null;
      }
    });
  }

  triggerNav(direction = 'next') {
    const btn = direction === 'next' ? this.nextButton : this.prevButton;
    if (!btn) return;

    const wasDisabledAttr = btn.hasAttribute('disabled');
    const wasDisabledProp = !!btn.disabled;
    if (wasDisabledAttr || wasDisabledProp) {
      btn.removeAttribute('disabled');
      btn.disabled = false;
    }

    const evt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
    Object.defineProperty(evt, 'currentTarget', { value: btn });
    Object.defineProperty(evt, 'target', { value: btn });

    this.onButtonClick(evt);
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item) => {
      const linkElements = item.querySelectorAll('a');
      const visible = this.isSlideVisible(item);

      if (visible) {
        if (linkElements.length) {
          linkElements.forEach((button) => button.removeAttribute('tabindex'));
        }
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length) {
          linkElements.forEach((button) => button.setAttribute('tabindex', '-1'));
        }
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class ScrollingPromotion extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.promotion = this.querySelector('.promotion');
    if (!this.promotion) return;
    this.init();
  }

  init() {
    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.classList.add('promotion-wrapper');

    // Move the original promotion inside wrapper
    wrapper.appendChild(this.promotion);
    this.appendChild(wrapper);

    // Wait for layout to render
    requestAnimationFrame(() => {
      const promoWidth = this.promotion.offsetWidth;
      const containerWidth = this.offsetWidth;

      if (!promoWidth || !containerWidth) {
        setTimeout(() => this.init(), 100);
        return;
      }

      const minTotalWidth = containerWidth * 2;
      let totalWidth = promoWidth;

      let safety = 0;
      while (totalWidth < minTotalWidth && safety < 50) {
        const clone = this.promotion.cloneNode(true);
        wrapper.appendChild(clone);
        totalWidth += promoWidth;
        safety++;
      }

      wrapper.style.setProperty('--scroll-distance', `${promoWidth}px`);
      this.play();
    });

    // Pause when out of viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.play();
          else this.pause();
        });
      },
      { rootMargin: '0px 0px 50px 0px' }
    );

    observer.observe(this);
  }

  play() {
    this.classList.remove('scrolling-promotion--paused');
  }

  pause() {
    this.classList.add('scrolling-promotion--paused');
  }
}

customElements.define('scrolling-promotion', ScrollingPromotion);

class BreadcrumbsNav extends HTMLElement {
  constructor() {
    super();
    this.separator = this.dataset.separatorIcon;
  }

  connectedCallback() {
    this.render();

    if (this.dataset.template.includes("collection")) {
      if (this.dataset.collectionHandle) {
        sessionStorage.setItem("lastCollection", JSON.stringify({
          handle: this.dataset.collectionHandle,
          title: this.dataset.collectionTitle,
          url: this.dataset.collectionUrl
        }));
      }
    }

    if (this.dataset.template.includes("product")) {
      const lastCollection = sessionStorage.getItem("lastCollection");
      if (lastCollection) {
        const col = JSON.parse(lastCollection);
        this.overrideCollection(col);
      }
    }
  }

  render() {
    const homeUrl = this.dataset.homeUrl;
    const template = this.dataset.template;

    let html = `<nav class="breadcrumbs" role="navigation" aria-label="breadcrumbs">`;
    html += `<a href="${homeUrl}" title="${window.breadCrumbs.home}">${window.breadCrumbs.home}</a>`;

    if (template.includes("page")) {
      html += this.sep() + `<span>${this.dataset.pageTitle}</span>`;
    }

    else if (template.includes("product")) {
      if (this.dataset.collectionUrl) {
        html += this.sep() + `<a href="${this.dataset.collectionUrl}">${this.dataset.collectionTitle}</a>`;
      }
      html += this.sep(true) + `<span class="breadcrumbs--last">${this.dataset.productTitle}</span>`;
    }

    else if (template.includes("collection")) {
      if (this.dataset.collectionHandle !== "all") {
        html += this.sep() + `<a href="${this.dataset.allCollectionsUrl}">${window.breadCrumbs.collection}</a>`;
      }
      html += this.sep(true) + `<span class="breadcrumbs--last">${this.dataset.collectionTitle}</span>`;
    }

    else if (template === "blog") {
      html += this.sep(true) + `<span>${this.dataset.blogTitle}</span>`;
    }

    else if (template === "article") {
      html += this.sep() + `<a href="${this.dataset.blogUrl}">${this.dataset.blogTitle}</a>`;
      html += this.sep(true) + `<span class="breadcrumbs--last">${this.dataset.articleTitle}</span>`;
    }

    else {
      html += this.sep(true) + `<span class="breadcrumbs--last">${this.dataset.pageTitle}</span>`;
    }

    html += `</nav>`;
    this.innerHTML = html;
  }

  sep(isLast = false) {
    return `<span aria-hidden="true" class="breadcrumbs--sep${isLast ? " breadcrumbs--sep-last" : ""}">${this.separator}</span>`;
  }

  overrideCollection(col) {
    const nav = this.querySelector(".breadcrumbs");
    if (!nav || !col.url) return;

    const last = nav.querySelector(".breadcrumbs--last");
    if (!last) return;

    const oldSep = nav.querySelector(".breadcrumbs--sep-last");
    if (oldSep) oldSep.remove();

    last.insertAdjacentHTML("beforebegin", 
      this.sep() + `<a href="${col.url}">${col.title}</a>` + this.sep(true)
    );
  }
}

customElements.define("breadcrumbs-nav", BreadcrumbsNav);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.url = this.dataset.url;
    this.productsToShow = parseInt(this.dataset.productsToShow) || 4;
    this.columnsDesktop = this.dataset.columnsDesktop || 4;
    this.columnsMobile = this.dataset.columnsMobile || 2;
  }

  connectedCallback() {
    this.trackProductView();
    this.renderRecentlyViewed();
  }

  trackProductView() {
    const viewed = JSON.parse(localStorage.getItem('recently-viewed') || '[]');
    const current = this.productId;

    if (current && !viewed.includes(current)) {
      viewed.unshift(current);
    }

    const unique = [...new Set(viewed)];
    const sliced = unique.slice(0, 20);
    localStorage.setItem('recently-viewed', JSON.stringify(sliced));
  }

  renderRecentlyViewed() {
    const viewed = JSON.parse(localStorage.getItem('recently-viewed') || '[]')
      .filter(id => id !== this.productId)
      .slice(0, this.productsToShow);

    if (!viewed.length) {
      return;
    }

    const query = viewed.map(id => `id:${id}`).join(' OR ');
    const searchUrl = `${this.url}${encodeURIComponent(query)}`;

    fetch(searchUrl)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const products = doc.querySelectorAll('[data-product-id]');

        if (!products.length) {
          return;
        }

        const ul = this.querySelector('ul');
        const slider = this.querySelector('slider-component');

        let count = 0;

        viewed.forEach(id => {
          for (const el of products) {
            if (el.dataset.productId === id && count < this.productsToShow) {
              ul.insertAdjacentHTML('beforeend', el.outerHTML);
              count++;
              break;
            }
          }
        });

        if (count > 0 && slider) {
          slider.appendChild(ul);
        } else if (count > 0) {
          this.appendChild(ul);
        } else {
          console.warn('[RecentlyViewed] No matched product elements rendered');
        }
      })
      .catch(err => {
        console.error('[RecentlyViewed] Error fetching search results:', err);
      });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class InteractiveMenu extends HTMLElement {
  constructor() {
    super();
    this.texts = [];
    this.images = [];
    this.mobileIndex = 0;
    this.mobileInterval = null;
  }

  connectedCallback() {
    this.texts = this.querySelectorAll(".interactive-menu--text");
    this.images = this.querySelectorAll(".interactive-menu--image");

    if (!this.images.length || !this.texts.length) return;

    this.showImage(this.images[0].dataset.tab);

    this.bindDesktopEvents();
    this.startMobileMode();

    window.addEventListener("resize", () => this.startMobileMode());
  }

  showImage(tabId) {
    this.images.forEach((img) => {
      img.style.opacity = 0;
      if (img.dataset.tab === tabId) {
        setTimeout(() => {
          img.style.opacity = 1;
        }, 10);
      }
    });

    this.texts.forEach((text) => {
      text.classList.remove("active");
      let bar = text.querySelector(".progress-bar");
      if (bar) {
        bar.style.width = "0%";
        bar.style.transition = "none";
      }

      if (text.dataset.tab === tabId) {
        text.classList.add("active");
        if (window.innerWidth <= 990) {
          let bar = text.querySelector(".progress-bar");
          if (bar) {
            void bar.offsetWidth;
            bar.style.transition = "width 6s linear";
            bar.style.width = "100%";
          }
        }
      }
    });
  }

  bindDesktopEvents() {
    this.texts.forEach((text) => {
      text.addEventListener("mouseenter", () => {
        if (window.innerWidth > 990) {
          this.showImage(text.dataset.tab);
        }
      });

      text.addEventListener("click", () => {
        if (text.dataset.link) {
          window.location.href = text.dataset.link;
        }
      });
    });
  }

  startMobileMode() {
    clearInterval(this.mobileInterval);

    if (window.innerWidth <= 990) {
      this.mobileIndex = 0;
      this.showImage(this.images[0].dataset.tab);

      this.mobileInterval = setInterval(() => {
        this.mobileIndex = (this.mobileIndex + 1) % this.images.length;
        this.showImage(this.images[this.mobileIndex].dataset.tab);
      }, 6000);
    }
  }
}

customElements.define("interactive-menu", InteractiveMenu);

class OnboardingPopup extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.slides = this.querySelectorAll('.onboarding_popup--slider');
    this.profile = this.querySelector('div[data-open-profile-info]');
    this.contact = this.querySelector('div[data-open-profile-contact]')
    this.previousButtons = this.querySelectorAll('.onboarding_popup--previous-step');
    this.nextButtons = this.querySelectorAll('.onboarding_popup--next-step');
    this.submitButtons = this.querySelectorAll('.onboarding_popup--submit-step');
    this.dotsContainer = this.querySelector('.onboarding_popup--dots');
    this.Form = this.querySelector('#customer-info-form button[type="submit"]');
    this.dots = [];
    this.init();
    this.setupPolicyLinks();
    this.openProfile();
    this.openContact();
    this.contactNotification();
    this.copyText();
  }

  init() {
    this.createDots();
    this.showSlide(this.currentSlide);
    this.previousButtons.forEach(button => {
      button.addEventListener('click', this.previousSlide.bind(this));
    });
    this.nextButtons.forEach(button => {
      button.addEventListener('click', this.nextSlide.bind(this));
    });
    this.submitButtons.forEach(button => {
      button.addEventListener('click', this.submitSlide.bind(this));
    });
  }

  createDots() {
    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('onboarding_popup--dot');
      if (index === this.currentSlide) {
        dot.classList.add('active');
      }
      dot.addEventListener('click', () => this.showSlide(index));
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    });
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => {
      slide.style.display = i === index ? 'flex' : 'none';
      slide.classList.toggle('active', i <= index);
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i <= index);
    });
    this.currentSlide = index;
  }

  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    this.showSlide(this.currentSlide);
  }

  previousSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.showSlide(this.currentSlide);
  }

  submitSlide() {
    this.Form.click();
  }

  skipToForm() {
    const formSlideIndex = Array.from(this.slides).findIndex(slide => slide.classList.contains('onboarding_popup--form'));
    if (formSlideIndex !== -1) {
      this.currentSlide = formSlideIndex;
      this.showSlide(this.currentSlide);
    }
  }

  setupPolicyLinks() {
    const policyContainer = this.querySelector(".form-for-marketing");
    if (!policyContainer) return;

    const links = policyContainer.querySelectorAll("a");

    links.forEach(link => {
      link.addEventListener("click", async (e) => {
        e.preventDefault();

        const url = link.href;
        if (!url) return;

        try {
          const response = await fetch(url);
          const htmlString = await response.text();

          const parser = new DOMParser();
          const html = parser.parseFromString(htmlString, "text/html");

          const mainContent = html.querySelector("#MainContent");

          this.renderPolicyPopup(mainContent ? mainContent.innerHTML : "No content found");
        } catch (err) {
          console.error("Failed to load policy page:", err);
        }
      });
    });
  }

  renderPolicyPopup(content) {
    const popup = document.createElement("div");
    popup.className = "policy-popup fixed inset-0 z-999999";
    popup.innerHTML = `
      <div class="policy-overlay absolute inset-0 bg-black-opacity-50 block!"></div>
      <div class="policy-box absolute top-half left-half translate-minus-half size-[screen-minus-5] md:w-80per md:max-w-150 x2xl:max-w-175 max-h-90vh bg-white rounded-1">
        <div class="w-full flex flex-col items-end md:pt-5 md:pl-5 md:pr-5 pt-2,5 pl-2,5 pr-2,5 pb-0">
          <button class="policy-close">
            <svg class="icon icon-close-20px w-2,5 h-2,5 text-primary-lighter" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5 5L15 15" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="policy-content">${content}</div>
      </div>
    `;

    document.body.appendChild(popup);

    popup.querySelector(".policy-close").onclick = () => popup.remove();
    popup.querySelector(".policy-overlay").onclick = () => popup.remove();
  }

  openProfile() {
    if (!this.profile) return;

    const dropdown = this.profile.nextElementSibling;
    if (!dropdown) return;

    const wrapper = this.profile.parentElement;
    const icon = this.profile.querySelector('.icon-arrow-down-head-20px');

    const open = () => {
      dropdown.classList.remove('invisible');
      wrapper?.classList.add('bg-primary-light');
      icon?.classList.add('rotate-180deg')
    };

    const close = () => {
      dropdown.classList.add('invisible');
      wrapper?.classList.remove('bg-primary-light');
      icon?.classList.remove('rotate-180deg')
    };

    const toggle = () => {
      const isHidden = dropdown.classList.contains('invisible');
      isHidden ? open() : close();
    };

    this.profile.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle();
    });

    dropdown.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    document.addEventListener('click', () => {
      close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  openContact() {
    if (!this.contact) return;

    const dropdown = this.contact.querySelector('div[data-profile-contact]');
    const close_el = this.contact.querySelectorAll('div[data-close-profile-contact]');
    if (!dropdown) return;

    const open = () => {
      dropdown.classList.remove('invisible');
    };

    const close = () => {
      dropdown.classList.add('invisible');
    };

    const toggle = () => {
      const isHidden = dropdown.classList.contains('invisible');
      isHidden ? open() : close();
    };

    this.contact.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle();
    });

    dropdown.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    close_el.forEach((el) => {
      el.addEventListener('click', () => {
        close();
      });
    })

    close_el.forEach((el) => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    })
  }

  contactNotification() {
    const to = window.notification.email,
          subject = window.notification.support_subject,
          body = window.notification.support_body,
          mail_default = this.querySelector('div[data-mail-default]'),
          mail_gmail = this.querySelector('div[data-mail-gmail]'),
          mail_outlook = this.querySelector('div[data-mail-outlook]');

    if (mail_default) {
      mail_default.addEventListener('click', () => {
        window.location.href = `mailto:${encodeURIComponent(to)}` + `?subject=${encodeURIComponent(subject)}` + `&body=${encodeURIComponent(body)}`;;
      })
    }

    if (mail_gmail) {
      mail_gmail.addEventListener('click', () => {
        let url_gmail = "https://mail.google.com/mail/?view=cm&fs=1" + `&to=${encodeURIComponent(to)}` + `&su=${encodeURIComponent(subject)}` + `&body=${encodeURIComponent(body)}`;
        window.open(url_gmail, "_blank", "noopener,noreferrer");
      })
    }

    if (mail_outlook) {
      mail_outlook.addEventListener('click', () => {
        let url_outlook =  "https://outlook.office.com/mail/deeplink/compose" + `?to=${encodeURIComponent(to)}` + `&subject=${encodeURIComponent(subject)}` + `&body=${encodeURIComponent(body)}`;
        window.open(url_outlook, "_blank", "noopener,noreferrer");
      })
    }
  }

  copyText() {
    const copy = this.querySelector('div[data-mail-copy]');

    if (!copy) return

    const button = copy.querySelector('div[data-mail-copy-button]'),
          email = copy.querySelector('div[data-mail-copy-email]').innerHTML;

    button.addEventListener('click', () => {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(email);

        copy.querySelector('span[data-mail-copy-label]').innerHTML = window.notification.copied;
        copy.querySelector('svg.icon-copy-20px').classList.add('invisible');
        copy.querySelector('svg.icon-check-20px').classList.remove('invisible');

        return;
      }
    })
  }
}

customElements.define('onboarding-popup', OnboardingPopup);

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.handleCloseClick = this.handleCloseClick.bind(this);
  }

  connectedCallback() {
    this.storageKey = this.getStorageKey();

    // If dismissed in this session → keep hidden
    if (sessionStorage.getItem(this.storageKey) === 'true') {
      return;
    }

    // Otherwise, show immediately
    this.show();

    const closeButton = this.querySelector('[data-close]');
    if (closeButton) {
      closeButton.addEventListener('click', this.handleCloseClick);
    }
  }

  disconnectedCallback() {
    const closeButton = this.querySelector('[data-close]');
    if (closeButton) {
      closeButton.removeEventListener('click', this.handleCloseClick);
    }
  }

  handleCloseClick() {
    sessionStorage.setItem(this.storageKey, 'true');
    this.hide();
  }

  show() {
    this.classList.add('block');
    this.classList.remove('invisible');
  }

  hide() {
    this.classList.add('invisible');
  }

  getStorageKey() {
    if (this.dataset.key) {
      return `announcementBarDismissed:${this.dataset.key}`;
    }
    if (this.id) {
      return `announcementBarDismissed:${this.id}`;
    }
    return `announcementBarDismissed:index-${Array.from(
      document.querySelectorAll('announcement-bar')
    ).indexOf(this)}`;
  }
}

customElements.define('announcement-bar', AnnouncementBar);